
import moment from 'moment';
import { action, observable } from 'mobx';
import {
	Model,
	IModelAttributes,
	attribute,
	entity,
	ReferencePath,
} from 'Models/Model';
import * as Models from 'Models/Entities';
import * as Validators from 'Validators';
import { CRUD } from '../CRUDOptions';
import * as AttrUtils from 'Util/AttributeUtils';
import { IAcl } from 'Models/Security/IAcl';
import {
	makeFetchOneToManyFunc,
	makeEnumFetchFunction,
	getCreatedModifiedCrudOptions,
} from 'Util/EntityUtils';
import VisitorsAtoFile from 'Models/Security/Acl/VisitorsAtoFile';
import UserAtoFile from 'Models/Security/Acl/UserAtoFile';
import * as Enums from '../Enums';
import { EntityFormMode } from 'Views/Components/Helpers/Common';
import SuperAdministratorScheme from '../Security/Acl/SuperAdministratorScheme';


export interface IAtoFileEntityAttributes extends IModelAttributes {
	atoProcessingStatus: Enums.processingStatus;
	fileDate: Date;
	fileName: string;
	fileKey: string;

	atoFileRecordss: Array<
		| Models.AtoRecordEntity
		| Models.IAtoRecordEntityAttributes
	>;

}

@entity('AtoFileEntity', 'ATO File')
export default class AtoFileEntity extends Model
	implements IAtoFileEntityAttributes {
	public static acls: IAcl[] = [
		new SuperAdministratorScheme(),
		new VisitorsAtoFile(),
		new UserAtoFile(),

	];

	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	@observable
	@attribute<AtoFileEntity, Enums.processingStatus>()
	@CRUD({
		name: 'ATO Processing Status',
		displayType: 'enum-combobox',
		order: 10,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: (attr: string) => {
			return AttrUtils.standardiseEnum(attr, Enums.processingStatusOptions);
		},
		enumResolveFunction: makeEnumFetchFunction(Enums.processingStatusOptions),
		displayFunction: (attr: Enums.processingStatus) => Enums.processingStatusOptions[attr],
	})
	public atoProcessingStatus: Enums.processingStatus;
	

	
	/**
	 * Target Year and Month for the data. Day should always be 0.
	 */
	@observable
	@attribute<AtoFileEntity, Date>({ serialise: (e, a) => AttrUtils.serialiseDate(a) })
	@CRUD({
		name: 'File Date',
		displayType: 'datepicker',
		order: 20,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: attr => AttrUtils.standardiseDate(attr, { serialiseAs: 'date' }),
	})
	public fileDate: Date;

	@observable
	@attribute<AtoFileEntity, string>()
	@CRUD({
		name: 'File Name',
		displayType: 'textfield',
		order: 30,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public fileName: string;

	@observable
	@attribute<AtoFileEntity, string>()
	@CRUD({
		name: 'File Key',
		displayType: 'textfield',
		order: 40,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public fileKey: string;

	@observable
	@attribute({ isReference: true, manyReference: true })
	@CRUD({
		
		name: 'ATO File Recordss',
		displayType: 'reference-multicombobox',
		order: 50,
		referenceTypeFunc: () => Models.AtoRecordEntity,
		disableDefaultOptionRemoval: true,
		referenceResolveFunction: makeFetchOneToManyFunc({
			relationName: 'atoFileRecordss',
			oppositeEntity: () => Models.AtoRecordEntity,
		}),
		
	})
	public atoFileRecordss: Models.AtoRecordEntity[] = [];



	// eslint-disable-next-line @typescript-eslint/no-useless-constructor
	constructor(attributes?: Partial<IAtoFileEntityAttributes>) {
		super(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	@action
	public assignAttributes(attributes?: Partial<IAtoFileEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.atoProcessingStatus !== undefined) {
				this.atoProcessingStatus = attributes.atoProcessingStatus;
			}
			if (attributes.fileDate !== undefined) {
				if (attributes.fileDate === null) {
					this.fileDate = attributes.fileDate;
				} else {
					this.fileDate = moment(attributes.fileDate).toDate();
				}
			}
			if (attributes.fileName !== undefined) {
				this.fileName = attributes.fileName;
			}
			if (attributes.fileKey !== undefined) {
				this.fileKey = attributes.fileKey;
			}
			if (attributes.atoFileRecordss !== undefined && Array.isArray(attributes.atoFileRecordss)) {
				for (const model of attributes.atoFileRecordss) {
					if (model instanceof Models.AtoRecordEntity) {
						this.atoFileRecordss.push(model);
					} else {
						this.atoFileRecordss.push(new Models.AtoRecordEntity(model));
					}
				}
			}

		}
	}

	/**
	 * Additional fields that are added to GraphQL queries when using the
	 * the managed model APIs.
	 */
	public defaultExpands = `
		atoFileRecordss {
			${Models.AtoRecordEntity.getAttributes().join('\n')}
		}
	`;

	/**
	 * The save method that is called from the admin CRUD components.
	 */
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	public async saveFromCrud(formMode: EntityFormMode) {
		const relationPath: ReferencePath = {
			atoFileRecordss: {},
		};
		return this.save(
			relationPath,
			{
				options: [
					{
						key: 'mergeReferences',
						graphQlType: '[String]',
						value: [
						],
					},
				],
			},
		);
	}

	/**
	 * Returns the string representation of this entity to display on the UI.
	 */
	public getDisplayName() {
		return this.id;
	}
}

/*
 * Retrieve the created and modified CRUD attributes for defining the CRUD views and decorate the class with them.
 */
const [createdAttr, modifiedAttr] = getCreatedModifiedCrudOptions();
CRUD(createdAttr)(AtoFileEntity.prototype, 'created');
CRUD(modifiedAttr)(AtoFileEntity.prototype, 'modified');
